/**
 * Excel-style formulas for cell tables.
 *
 * A cell whose text starts with `=` is a formula. It may reference:
 *   - other cells:  R1C2          (1-based row / column)
 *   - cell ranges:  R1C2:R3C2     (rectangular block, only as a function arg)
 *   - data tokens:  {{netAmount}} (resolved to a number)
 * and combine them with `+ - * /`, parentheses, unary minus, and the
 * functions SUM, SUBTRACT, AVG, MIN, MAX, ROUND, ABS.
 *
 *   =ROUND(SUM(R1C2:R3C2) * {{vatPercent}} / 100, 2)
 *
 * Evaluation is a hand-written recursive-descent parser — deliberately NOT
 * `eval`/`new Function`, both for safety and because the app's CSP
 * (`script-src 'self'`) blocks runtime code generation. Any malformed
 * formula, circular reference, or out-of-range ref resolves to NaN, which the
 * caller renders as blank.
 */
import type { TokenContext } from './dataTokens';
import { resolveText } from './dataTokens';
import { formatNumber } from './currency';

export type CellFormatKind = 'number' | 'currency' | 'percent' | 'raw';

/** A cell is a formula when its (trimmed) text begins with `=`. */
export function isFormula(text: string | undefined): boolean {
  return typeof text === 'string' && text.trimStart().startsWith('=');
}

/** Parse a possibly-formatted string ("Rs. 1,500.00", "12%") to a number. */
export function parseNumeric(s: string): number {
  if (s == null) return NaN;
  // Keep digits, dot and a leading minus; drop commas, currency, %, spaces.
  const cleaned = String(s)
    .replace(/,/g, '')
    .replace(/[^0-9.\-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return NaN;
  return Number(cleaned);
}

// ── Tokenizer ────────────────────────────────────────────────────────────────
type Tok =
  | { t: 'num'; v: number }
  | { t: 'ref'; r: number; c: number } // 0-based
  | { t: 'range'; r1: number; c1: number; r2: number; c2: number }
  | { t: 'ident'; v: string }
  | { t: 'op'; v: '+' | '-' | '*' | '/' }
  | { t: 'lp' }
  | { t: 'rp' }
  | { t: 'comma' };

const RANGE_RE = /^R(\d+)C(\d+):R(\d+)C(\d+)/i;
const REF_RE = /^R(\d+)C(\d+)/i;
const NUM_RE = /^\d*\.?\d+/;
const IDENT_RE = /^[A-Za-z]+/;

function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let s = src;
  while (s.length) {
    if (s[0] === ' ' || s[0] === '\t' || s[0] === '\n') {
      s = s.slice(1);
      continue;
    }
    let m: RegExpExecArray | null;
    if ((m = RANGE_RE.exec(s))) {
      toks.push({
        t: 'range',
        r1: +m[1] - 1,
        c1: +m[2] - 1,
        r2: +m[3] - 1,
        c2: +m[4] - 1,
      });
    } else if ((m = REF_RE.exec(s))) {
      toks.push({ t: 'ref', r: +m[1] - 1, c: +m[2] - 1 });
    } else if ((m = NUM_RE.exec(s))) {
      toks.push({ t: 'num', v: Number(m[0]) });
    } else if ((m = IDENT_RE.exec(s))) {
      toks.push({ t: 'ident', v: m[0].toUpperCase() });
    } else if (s[0] === '+' || s[0] === '-' || s[0] === '*' || s[0] === '/') {
      toks.push({ t: 'op', v: s[0] as '+' });
      m = null;
      s = s.slice(1);
      continue;
    } else if (s[0] === '(') {
      toks.push({ t: 'lp' });
      s = s.slice(1);
      continue;
    } else if (s[0] === ')') {
      toks.push({ t: 'rp' });
      s = s.slice(1);
      continue;
    } else if (s[0] === ',') {
      toks.push({ t: 'comma' });
      s = s.slice(1);
      continue;
    } else {
      throw new Error(`Unexpected character: ${s[0]}`);
    }
    s = s.slice(m[0].length);
  }
  return toks;
}

// ── Evaluation context ─────────────────────────────────────────────────────────
interface EvalState {
  cells: string[][];
  ctx: TokenContext;
  /** Cells currently mid-evaluation, for circular-reference detection. */
  visiting: Set<string>;
}

/** Resolve a single cell (0-based) to a number, recursing into nested formulas. */
function cellValue(r: number, c: number, st: EvalState): number {
  const raw = st.cells[r]?.[c];
  if (raw == null || raw === '') return 0;
  if (isFormula(raw)) {
    const key = `${r}:${c}`;
    if (st.visiting.has(key)) return NaN; // circular
    st.visiting.add(key);
    const v = evalExpr(raw.trimStart().slice(1), st);
    st.visiting.delete(key);
    return v;
  }
  // Plain value: resolve any {{tokens}} then parse to a number.
  return parseNumeric(resolveText(raw, st.ctx));
}

function rangeValues(
  r1: number,
  c1: number,
  r2: number,
  c2: number,
  st: EvalState,
): number[] {
  const out: number[] = [];
  const [rl, rh] = r1 <= r2 ? [r1, r2] : [r2, r1];
  const [cl, ch] = c1 <= c2 ? [c1, c2] : [c2, c1];
  for (let r = rl; r <= rh; r++) {
    for (let c = cl; c <= ch; c++) out.push(cellValue(r, c, st));
  }
  return out;
}

// ── Recursive-descent parser ───────────────────────────────────────────────────
// expr   = term (('+'|'-') term)*
// term   = factor (('*'|'/') factor)*
// factor = ('-'|'+') factor | num | ref | func '(' args ')' | '(' expr ')'
class Parser {
  private i = 0;
  constructor(
    private toks: Tok[],
    private st: EvalState,
  ) {}

  private peek(): Tok | undefined {
    return this.toks[this.i];
  }
  private next(): Tok {
    const t = this.toks[this.i++];
    if (!t) throw new Error('Unexpected end of formula');
    return t;
  }

  parse(): number {
    const v = this.expr();
    if (this.i !== this.toks.length) throw new Error('Trailing tokens');
    return v;
  }

  private expr(): number {
    let v = this.term();
    let t = this.peek();
    while (t && t.t === 'op' && (t.v === '+' || t.v === '-')) {
      this.next();
      const rhs = this.term();
      v = t.v === '+' ? v + rhs : v - rhs;
      t = this.peek();
    }
    return v;
  }

  private term(): number {
    let v = this.factor();
    let t = this.peek();
    while (t && t.t === 'op' && (t.v === '*' || t.v === '/')) {
      this.next();
      const rhs = this.factor();
      v = t.v === '*' ? v * rhs : rhs === 0 ? NaN : v / rhs;
      t = this.peek();
    }
    return v;
  }

  private factor(): number {
    const t = this.next();
    switch (t.t) {
      case 'op':
        if (t.v === '-') return -this.factor();
        if (t.v === '+') return this.factor();
        throw new Error(`Unexpected operator ${t.v}`);
      case 'num':
        return t.v;
      case 'ref':
        return cellValue(t.r, t.c, this.st);
      case 'lp': {
        const v = this.expr();
        if (this.next().t !== 'rp') throw new Error('Expected )');
        return v;
      }
      case 'ident':
        return this.func(t.v);
      default:
        throw new Error('Unexpected token in expression');
    }
  }

  /** Collect a function's argument values, expanding ranges into many numbers. */
  private args(): number[] {
    const out: number[] = [];
    if (this.peek()?.t === 'rp') return out;
    for (;;) {
      const t = this.peek();
      if (t && t.t === 'range') {
        this.next();
        out.push(...rangeValues(t.r1, t.c1, t.r2, t.c2, this.st));
      } else {
        out.push(this.expr());
      }
      if (this.peek()?.t === 'comma') {
        this.next();
        continue;
      }
      break;
    }
    return out;
  }

  private func(name: string): number {
    if (this.next().t !== 'lp') throw new Error(`Expected ( after ${name}`);
    const a = this.args();
    if (this.next().t !== 'rp') throw new Error(`Expected ) closing ${name}`);
    switch (name) {
      case 'SUM':
        return a.reduce((s, n) => s + n, 0);
      case 'SUBTRACT':
      case 'SUB':
        // First value minus every following one: SUBTRACT(R1C2, R2C2, …).
        return a.length ? a.slice(1).reduce((s, n) => s - n, a[0]) : NaN;
      case 'AVG':
      case 'AVERAGE':
        return a.length ? a.reduce((s, n) => s + n, 0) / a.length : NaN;
      case 'MIN':
        return a.length ? Math.min(...a) : NaN;
      case 'MAX':
        return a.length ? Math.max(...a) : NaN;
      case 'ABS':
        return Math.abs(a[0] ?? NaN);
      case 'ROUND': {
        const n = a[0] ?? NaN;
        const d = a.length > 1 ? Math.trunc(a[1]) : 0;
        const f = Math.pow(10, d);
        return Math.round(n * f) / f;
      }
      default:
        throw new Error(`Unknown function ${name}`);
    }
  }
}

/** Evaluate a formula body (the part after `=`) against the grid + token ctx. */
function evalExpr(body: string, st: EvalState): number {
  // Resolve {{tokens}} to numeric literals first so the lexer sees plain
  // numbers. Wrap in parens to keep negatives well-formed: `(-5)`.
  const withTokens = body.replace(/\{\{([^}]+)\}\}/g, (_m, k) => {
    const v = parseNumeric(resolveText(`{{${String(k).trim()}}}`, st.ctx));
    return `(${isFinite(v) ? v : 0})`;
  });
  try {
    return new Parser(tokenize(withTokens), st).parse();
  } catch {
    return NaN;
  }
}

/**
 * Public entry: evaluate a cell formula (text including the leading `=`).
 * Returns NaN on any error / cycle / empty result.
 */
export function evaluateCellFormula(
  formula: string,
  cells: string[][],
  ctx: TokenContext,
): number {
  const body = formula.trimStart().replace(/^=/, '');
  return evalExpr(body, { cells, ctx, visiting: new Set() });
}

/**
 * Format a computed result per the cell's chosen format, padding the fractional
 * part to `decimals` places with trailing zeros (e.g. 16 → "16.00").
 */
export function formatCellResult(
  n: number,
  kind: CellFormatKind = 'number',
  decimals = 3,
): string {
  if (!isFinite(n)) return '';
  switch (kind) {
    case 'percent':
      return `${formatNumber(n, 'indian', decimals)}%`;
    case 'raw':
      // No thousands grouping, but still padded to the chosen decimals.
      return n.toFixed(Math.max(0, Math.trunc(decimals)));
    case 'currency':
    case 'number':
    default:
      return formatNumber(n, 'indian', decimals);
  }
}
